import React, { useEffect, useState, useRef } from "react";
import client from "@culturehq/client";
import { Icon, ModalDialog, PlainButton } from "@culturehq/components";
import { useDoor, useSortable } from "app/hooks";
import { errorService, t, Trans } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import ChqModalDialog from "components/common/ChqModalDialog";
import PeopleCards from "./PeopleCards";
import SortBySelected from "./SortBySelected";
import ProfileSearchBar from "./ProfileSearchBar";
import DisplayUsers from "./DisplayUsers";

const PeopleCardsArrangement = ({
  loading, landingPage, usersState, filter, onLandingPageUpdated
}) => {
  const [users, setUsers] = useState([]);

  useEffect(
    () => {
      setUsers(usersState.filter(user => user.selected));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usersState, filter]
  );

  const sortableRef = useSortable({
    handle: ".sortable__dragger",
    ghostClass: "ghost",
    onSort: async event => {
      const order = Array.from(event.to.children).map(element => (
        element.getAttribute("data-id")
      ));
      const { landingPage } = await client.reorderLandingPageUsers({ order });
      onLandingPageUpdated(landingPage);
    }
  });

  return (
    <div className="clearfix" ref={sortableRef}>
      <PeopleCards
        users={users}
        loading={loading}
        sortable
      />
    </div>
  );
};

const ApproveProfileButton = ({ onOpen, selectedUsers }) => (
  <PlainButton className="Approve-Button" onClick={onOpen}>
    <DisplayUsers selectedUsers={selectedUsers} />
  </PlainButton>
);

const ApproveProfileModal = ({
  initialOpen = false, landingPage, onSelectedUsers, onLandingPageUpdated
}) => {
  const isMounted = useRef(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [usersState, setUsersState] = useState([]);
  const [pagination, setPagination] = useState({});
  const [editArrengement, setEditArrengement] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [orderBy, setOrderBy] = useState(undefined);
  const [filter, setFilter] = useState(undefined);
  const { open, onOpen, onClose } = useDoor(initialOpen);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const getUsersWithSelectedValue = users => {
      if (!landingPage || !landingPage.users || !landingPage.users.length === 0) {
        return users;
      }

      return (
        landingPage.users.length > 0
          ? users.map(user => (
            {
              ...user,
              selected: !!landingPage?.users.find(selectedUser => selectedUser.id === user.id)
            }
          )) : users
      );
    };

    const fetchUsers = async () => {
      setLoading(true);

      try {
        const { users, pagination } = await client.listUsers({ page });
        if (!isMounted.current) return;

        setUsersState(getUsersWithSelectedValue(users));
        setPagination(pagination);
        setLoading(false);
      } catch (error) {
        errorService.error(error);
        if (!isMounted.current) return;

        setLoading(false);
      }
    };

    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (landingPage?.users?.length > 0) {
      setSelectedUsers(landingPage.users);
    }
  }, [landingPage]);

  const getUsersWithSelectedValue = users => (
    selectedUsers.length > 0
      ? users.map(user => (
        { ...user, selected: !!selectedUsers.find(selectedUser => selectedUser.id === user.id) }
      )) : users
  );

  const fetchUsers = async () => {
    setLoading(true);

    try {
      const { users, pagination } = await client.listUsers({ page });
      if (!isMounted.current) return;

      setUsersState(sort(getUsersWithSelectedValue(users)));
      setPagination(pagination);
      setLoading(false);
    } catch (error) {
      errorService.error(error);
      if (!isMounted.current) return;

      setLoading(false);
    }
  };

  const handlePageSelected = page => {
    setPage(page);
  };

  const sort = users => {
    if (orderBy === "selected") {
      return [...users.sort((a, b) => {
        if (a.selected === b.selected) {
          return 0;
        }

        return (a.selected ? -1 : 1);
      })];
    }

    if (orderBy === "unselected") {
      return [...users.sort((a, b) => {
        if (a.selected === b.selected) {
          return 0;
        }

        return (a.selected ? 1 : -1);
      })];
    }

    return users;
  };

  const handleResults = ({ users, pagination, query }) => {
    setFilter(query);
    if (!query) {
      fetchUsers();
    } else {
      setUsersState(sort(getUsersWithSelectedValue(users)));
      setPagination(pagination);
    }
  };

  const handleSelected = async changedUser => {
    if (landingPage) {
      await client.createLandingPageProfile({
        landingPageId: landingPage.id,
        userId: changedUser.id
      });
    }
    const selected = [...selectedUsers, changedUser];
    setSelectedUsers(selected);
    onSelectedUsers(selected);
    setUsersState(usersState.map(
      user => (user.id === changedUser.id ? { ...user, selected: true } : user)
    ));
  };

  const handleUnselected = async changedUser => {
    await client.deleteLandingPageProfile({
      landingPageId: landingPage.id,
      userId: changedUser.id
    });

    const selected = selectedUsers.filter(user => user.id !== changedUser.id);
    setSelectedUsers(selected);
    onSelectedUsers(selected);
    setUsersState(usersState.map(
      user => (user.id === changedUser.id ? { ...user, selected: false } : user)
    ));
  };

  const handleCloseModal = () => {
    fetchUsers();
    setEditArrengement(false);
    onClose();
  };

  const handleEditArrengement = () => {
    setEditArrengement(!editArrengement);
  };

  return (
    <>
      {open && (
        <ChqModalDialog onClose={handleCloseModal} className="ApproveProfileModal" entrance="zoomIn" ariaLabelledBy="approve-profile-title" ariaDescribedby="approve-profile-title">
          <ModalDialog.Heading titleId="approve-profile-title" onClose={handleCloseModal}>
            <Trans>Approve Profile Spotlights</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <div className="ApproveProfileModal__Body--Top">
              <DisplayUsers selectedUsers={selectedUsers} />
              <PlainButton onClick={handleEditArrengement}>
                {editArrengement ? (
                  <>
                    <Icon icon="close" />
                    <Trans>Close</Trans>
                  </>
                ) : (
                  <>
                    <Icon icon="edit" />
                    <Trans>Edit Arrangement</Trans>
                  </>
                )}
              </PlainButton>
            </div>
            <hr className="row Separator" />
            <div className="People__cards row">
              <div className="col-xs-12">
                <div className="row">
                  <div className="col-xs-8 SearchBar">
                    <ProfileSearchBar
                      onResults={handleResults}
                      page={page}
                    />
                  </div>
                  <div className="col-xs-4">
                    <SortBySelected
                      values={usersState}
                      setValues={setUsersState}
                      setOrderBy={setOrderBy}
                      disabled={editArrengement}
                    />
                  </div>
                </div>
              </div>
              <div className="col-xs-12">
                <div className="row">
                  <ErrorBoundary>
                    {editArrengement ? (
                      <PeopleCardsArrangement
                        loading={loading}
                        landingPage={landingPage}
                        usersState={usersState}
                        filter={filter}
                        onLandingPageUpdated={onLandingPageUpdated}
                      />
                    ) : (
                      <PeopleCards
                        users={usersState}
                        loading={loading}
                        pagination={pagination}
                        onPageSelected={handlePageSelected}
                        onSelected={handleSelected}
                        onUnselected={handleUnselected}
                      />
                    )}
                  </ErrorBoundary>
                </div>
              </div>
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
      <ApproveProfileButton
        onOpen={onOpen}
        selectedUsers={selectedUsers}
      />
    </>
  );
};

export default ApproveProfileModal;
